import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const fourViews = [
  { label: '专家', focus: '理论主线是否讲透，校内数据是否只作现象展示', icon: 'ri-user-star-line', color: 'primary' },
  { label: '学生', focus: '材料是否与自身经验相关，是否帮助理解问题', icon: 'ri-user-heart-line', color: 'accent' },
  { label: 'AI', focus: '整理课堂片段、问卷数据、学生回应和宏观资料', icon: 'ri-brain-line', color: 'primary' },
  { label: '教师', focus: '检查微观材料代表性，决定怎样优化问题链', icon: 'ri-user-settings-line', color: 'accent' },
];

const dataLinks = [
  '校内数据',
  '国家统计',
  '权威智库',
  '高校研究',
  '理论阐释',
  '教学改进',
];

export default function P42CaseCampusSurvey({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);

  return (
    <div className="relative flex flex-col min-h-screen justify-center overflow-hidden bg-background-50">
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-16 pb-8 md:pb-12">
        {/* 标题 */}
        <div ref={titleRef} className="text-center mb-5 md:mb-6">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100/60 text-accent-700 text-[11px] font-body font-semibold mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
            <i className="ri-file-text-line text-xs" /> 完整闭环案例 C
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            从<span className="text-primary-600">校内调研</span>到多视角教研
          </h2>
        </div>

        {/* 第一层：核心起点 */}
        <div className="flex justify-center mb-4 md:mb-5">
          <div className={`flex items-center gap-3 px-5 py-2.5 rounded-full bg-accent-50 border border-accent-200/50 transition-all duration-700 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '200ms' }}>
            <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-survey-line text-sm text-accent-600" />
            </div>
            <span className="text-sm md:text-base font-body font-bold text-foreground-800">校内文化自信调研</span>
          </div>
        </div>

        {/* 向下箭头 */}
        <div className="flex justify-center mb-4 md:mb-5">
          <div className="flex flex-col items-center">
            <div className="w-px h-4 md:h-5 bg-gradient-to-b from-accent-200 to-transparent" />
            <i className="ri-arrow-down-line text-accent-400 text-sm" />
          </div>
        </div>

        {/* 第二层：四视角卡片 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-5">
          {fourViews.map((view, i) => (
            <div
              key={view.label}
              className={`p-4 md:p-5 rounded-xl border transition-all duration-500 hover:scale-[1.02] hover:-translate-y-0.5 ${
                view.color === 'primary'
                  ? 'bg-primary-50/40 border-primary-200/40 hover:border-primary-300/50 hover:bg-primary-50/60'
                  : 'bg-accent-50/40 border-accent-200/40 hover:border-accent-300/50 hover:bg-accent-50/60'
              } ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${300 + i * 80}ms` }}
            >
              <div className={`w-9 h-9 mx-auto mb-2 rounded-xl flex items-center justify-center ${view.color === 'primary' ? 'bg-primary-100 border border-primary-200/60' : 'bg-accent-100 border border-accent-200/60'}`}>
                <i className={`${view.icon} text-sm ${view.color === 'primary' ? 'text-primary-600' : 'text-accent-600'}`} />
              </div>
              <p className="text-sm font-body font-bold text-foreground-800 text-center mb-1.5">{view.label}</p>
              <p className="text-[11px] md:text-xs text-foreground-500 font-body leading-snug text-center">{view.focus}</p>
            </div>
          ))}
        </div>

        {/* 向下箭头 */}
        <div className="flex justify-center mb-4 md:mb-5">
          <div className="flex flex-col items-center">
            <div className="w-px h-4 md:h-5 bg-gradient-to-b from-foreground-200 to-transparent" />
            <i className="ri-arrow-down-line text-foreground-300 text-sm" />
          </div>
        </div>

        {/* 第三层：数据链路 */}
        <div className="mb-5 md:mb-6">
          <p className={`text-center text-[11px] text-foreground-400 font-body mb-3 uppercase tracking-wider ${titleVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '700ms' }}>
            数据整合路径
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2">
            {dataLinks.map((item, i) => (
              <div key={item} className={`flex items-center transition-all duration-500 ${titleVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: `${700 + i * 60}ms` }}>
                <span className="px-3.5 py-2 rounded-lg bg-background-100 border border-background-200/60 text-xs font-body font-semibold text-foreground-700 whitespace-nowrap">
                  {item}
                </span>
                {i < dataLinks.length - 1 && (
                  <div className="flex items-center px-1">
                    <div className="w-3 h-px bg-foreground-300/40" />
                    <i className="ri-arrow-right-s-line text-foreground-300 text-xs" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 最终输出 */}
        <div className={`max-w-2xl mx-auto transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="relative p-5 md:p-6 rounded-xl bg-accent-50/50 border border-accent-200/40">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-accent-100 border border-accent-200/50">
              <span className="text-[10px] font-body font-semibold text-accent-700">最终输出</span>
            </div>
            <p className="text-sm md:text-base text-foreground-700 font-body leading-relaxed text-center pt-1">
              校内调研从一次课堂材料，转化为能够连接学生经验、社会事实和理论阐释的
              <strong className="text-accent-700">教学资源</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}